import React, { useState, useCallback, useMemo, useEffect } from "react";
import {
  Upload,
  X,
  FileImage,
  AlertCircle,
  CheckCircle,
  AlertTriangle,
  Eye,
  Trash2,
  Edit2,
  Save,
  XCircle,
} from "lucide-react";
import demoImageUrl from "./testImage.png";

const DEMO = false;

// 위험도 색상 테이블은 여러 곳에서 쓰이므로 파일 상단으로 승격
const RISK_COLORS = {
  low: { bg: "#dcfce7", border: "#22c55e", text: "#166534", label: "낮음" },
  medium: { bg: "#fef9c3", border: "#eab308", text: "#854d0e", label: "보통" },
  high: { bg: "#fee2e2", border: "#ef4444", text: "#991b1b", label: "높음" },
};

// 간단한 플레이스홀더 이미지 (SVG Data URI)
const ph = (w = 640, h = 360, label = "Demo Image") =>
  `data:image/svg+xml;utf8,` +
  encodeURIComponent(
    `<svg xmlns='http://www.w3.org/2000/svg' width='${w}' height='${h}'>\n` +
      `<defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0%' stop-color='#e0f2fe'/><stop offset='100%' stop-color='#ddd6fe'/></linearGradient></defs>` +
      `<rect width='100%' height='100%' fill='url(#g)'/>` +
      `<g fill='#111827' font-size='20' font-family='Arial, Helvetica, sans-serif'>` +
      `<text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle'>${label}</text>` +
      `</g>` +
      `</svg>`
  );

// ===== 공통 스타일/상수 (컴포넌트 밖으로 승격해 재렌더마다 재생성 방지) =====
const container = { maxWidth: 1120, margin: "0 auto", padding: "0 16px" };
const card = {
  background: "#fff",
  borderRadius: 16,
  boxShadow: "0 10px 25px rgba(0,0,0,0.08)",
};
const pad = { padding: 24 };
const hstack = (gap = 8, justify = "flex-start") => ({
  display: "flex",
  alignItems: "center",
  gap,
  justifyContent: justify,
});
const vstack = (gap = 8) => ({ display: "grid", gap });
const title = { margin: 0, fontWeight: 700, color: "#111827" };
const text = { margin: 0, color: "#4b5563" };
const btn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 16px",
  borderRadius: 10,
  cursor: "pointer",
  border: "none",
};
const btnPrimary = { ...btn, background: "#3b82f6", color: "#fff" };
const btnGray = { ...btn, background: "#6b7280", color: "#fff" };
const btnGhost = {
  ...btn,
  background: "#fff",
  color: "#111",
  border: "1px solid rgba(0,0,0,0.08)",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
};

const App = () => {
  // ===== 상태 =====
  const demoImages = [
    {
      id: 102,
      preview: ph(1280, 720, "US Demo 2"),
      name: "us-demo-2.png",
      size: 234567,
      type: "초음파",
    },
    {
      id: 103,
      preview: ph(1280, 720, "US Demo 3"),
      name: "us-demo-3.png",
      size: 345678,
      type: "초음파",
    },
  ];

  const [uploadedImages, setUploadedImages] = useState(DEMO ? demoImages : []);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(DEMO ? true : false);
  const [overallResult, setOverallResult] = useState(
    DEMO
      ? {
          risk: "medium",
          colors: RISK_COLORS,
          recommendation: "추가 검사를 고려해보세요",
        }
      : null
  );
  const [individualResults, setIndividualResults] = useState(
    DEMO
      ? [
          {
            id: 101,
            condition: "경미한 회전근개 손상",
            riskLevel: "medium",
            confidence: 88,
            findings: ["회전근개 두께 확인", "견봉하 간격 정상"],
          },
          {
            id: 102,
            condition: "정상",
            riskLevel: "low",
            confidence: 95,
            findings: ["회전근개 위치 정상"],
          },
          {
            id: 103,
            condition: "회전근개염(건염) 의심",
            riskLevel: "high",
            confidence: 82,
            findings: ["회전근개 건염 소견", "추가 검사가 필요"],
          },
        ]
      : []
  );
  const [isEditingOverall, setIsEditingOverall] = useState(false);
  const [editingImageId, setEditingImageId] = useState(null);
  const [tempEditData, setTempEditData] = useState({});
  const [fullscreenImage, setFullscreenImage] = useState(null);

  // ===== 업로드 이미지 맵 (id -> 이미지) =====
  const imageMap = useMemo(
    () => new Map(uploadedImages.map((i) => [i.id, i])),
    [uploadedImages]
  );

  // ===== 유틸 함수 =====
  const simulateAnalysis = () => {
    const conditions = [
      "정상",
      "경미한 회전근개 손상",
      "회전근개 부분 파열",
      "회전근개 전층 파열",
      "회전근개 석회화",
      "회전근개염(건염) 초기 소견",
      "회전근개-견봉 충돌 증후군",
    ];
    const riskLevels = ["low", "medium", "high"];
    const imageResults = uploadedImages.map((image) => {
      const condition =
        conditions[Math.floor(Math.random() * conditions.length)];
      const riskLevel =
        riskLevels[Math.floor(Math.random() * riskLevels.length)];
      const confidence = Math.floor(Math.random() * 20) + 80; // 80-99
      return {
        id: image.id,
        condition,
        riskLevel,
        confidence,
        findings: [
          "회전근개 위치 정상",
          "회전근개 두께 측정 완료",
          "견봉하 간격 확인됨",
        ].slice(0, Math.floor(Math.random() * 3) + 1),
      };
    });
    const highestRisk = imageResults.reduce(
      (hi, r) =>
        ({ low: 0, medium: 1, high: 2 }[r.riskLevel] >
        { low: 0, medium: 1, high: 2 }[hi]
          ? r.riskLevel
          : hi),
      "low"
    );
    return { imageResults, overallRisk: highestRisk };
  };

  const handleFileUpload = useCallback((event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    if (file.type && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const newImage = {
          id: Date.now() + Math.random(),
          file,
          preview: e.target.result,
          name: file.name,
          size: file.size,
          type: "초음파",
        };
        // 항상 1개만 유지
        setUploadedImages([newImage]);
      };
      reader.readAsDataURL(file);
    }

    // 같은 파일을 다시 선택할 수 있게 값 초기화
    event.target.value = "";
  }, []);

  const loadDemoImage = useCallback(() => {
    const newImage = {
      id: Date.now() + Math.random(),
      file: null,
      preview: demoImageUrl, // 번들된 실제 파일 URL 사용
      name: "testImage.png",
      size: 0,
      type: "이미지",
    };
    setUploadedImages([newImage]);
  }, []);

  useEffect(() => {
    if (uploadedImages.length === 1 && !analysisComplete && !isAnalyzing) {
      handleAnalysis();
    }
  }, [uploadedImages]);

  const removeImage = (id) => {
    setUploadedImages((prev) => prev.filter((img) => img.id !== id));
    if (uploadedImages.length === 1) {
      setAnalysisComplete(false);
      setOverallResult(null);
      setIndividualResults([]);
    }
  };

  const handleAnalysis = () => {
    if (uploadedImages.length === 0) return; // 이미지 없으면 중단
    setIsAnalyzing(true);
    setTimeout(() => {
      const results = simulateAnalysis();
      setIndividualResults(results.imageResults);
      setOverallResult({
        risk: results.overallRisk,
        colors: RISK_COLORS,
        recommendation:
          results.overallRisk === "high"
            ? "즉시 전문의 상담을 권장합니다"
            : results.overallRisk === "medium"
            ? "추가 검사를 고려해보세요"
            : "정기적인 관찰을 권장합니다",
      });
      setAnalysisComplete(true);
      setIsAnalyzing(false);
    }, 1200);
  };

  const resetAnalysis = () => {
    setUploadedImages([]);
    setAnalysisComplete(false);
    setOverallResult(null);
    setIndividualResults([]);
    setIsEditingOverall(false);
    setEditingImageId(null);
    setTempEditData({});
  };

  // ===== 편집 핸들러 =====
  const startEditingOverall = () => {
    setIsEditingOverall(true);
    setTempEditData({
      risk: overallResult.risk,
      recommendation: overallResult.recommendation,
    });
  };
  const saveOverallEdit = () => {
    setOverallResult((prev) => ({
      ...prev,
      risk: tempEditData.risk,
      recommendation: tempEditData.recommendation,
    }));
    setIsEditingOverall(false);
    setTempEditData({});
  };
  const cancelOverallEdit = () => {
    setIsEditingOverall(false);
    setTempEditData({});
  };

  const startEditingImage = (imageId) => {
    const r = individualResults.find((x) => x.id === imageId);
    setEditingImageId(imageId);
    setTempEditData({
      condition: r.condition,
      riskLevel: r.riskLevel,
      confidence: r.confidence,
      findings: [...r.findings],
    });
  };
  const saveImageEdit = () => {
    let nextResultsRef = null;
    setIndividualResults((prev) => {
      const next = prev.map((r) =>
        r.id === editingImageId
          ? {
              ...r,
              condition: tempEditData.condition,
              riskLevel: tempEditData.riskLevel,
              confidence: tempEditData.confidence,
              findings: [...tempEditData.findings],
            }
          : r
      );
      nextResultsRef = next;
      return next;
    });

    // 다음 틱에서 최신 결과로 전체 위험도 동기화
    setTimeout(() => {
      if (!nextResultsRef) return;
      const highestRisk = nextResultsRef.reduce(
        (hi, r) =>
          ({ low: 0, medium: 1, high: 2 }[r.riskLevel] >
          { low: 0, medium: 1, high: 2 }[hi]
            ? r.riskLevel
            : hi),
        "low"
      );
      if (overallResult && highestRisk !== overallResult.risk) {
        const rec =
          highestRisk === "high"
            ? "즉시 전문의 상담을 권장합니다"
            : highestRisk === "medium"
            ? "추가 검사를 고려해보세요"
            : "정기적인 관찰을 권장합니다";
        setOverallResult((prev) => ({
          ...prev,
          risk: highestRisk,
          recommendation: rec,
        }));
      }
    }, 0);

    setEditingImageId(null);
    setTempEditData({});
  };
  const cancelImageEdit = () => {
    setEditingImageId(null);
    setTempEditData({});
  };
  const handleImageClick = (imageId) => {
    const img = imageMap.get(imageId);
    if (!img) return;
    setFullscreenImage(img);
  };
  // 전체화면 뷰어 유틸: ESC로 닫기 + 배경 스크롤 잠금
  useEffect(() => {
    if (!fullscreenImage) return;

    const onKey = (e) => {
      if (e.key === "Escape") setFullscreenImage(null);
    };
    window.addEventListener("keydown", onKey);

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [fullscreenImage]);

  const closeFullscreen = () => setFullscreenImage(null);

  const getRiskIcon = (risk) =>
    risk === "high" ? (
      <AlertCircle size={20} />
    ) : risk === "medium" ? (
      <AlertTriangle size={20} />
    ) : (
      <CheckCircle size={20} />
    );

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background:
          "linear-gradient(135deg, #eff6ff 0%, #ffffff 50%, #eef2ff 100%)",
      }}
    >
      <header
        style={{
          background: "#ffffff",
          boxShadow:
            "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)",
          borderBottom: "4px solid #3b82f6",
        }}
      >
        <div style={{ ...container, paddingTop: 24, paddingBottom: 24 }}>
          <div style={hstack(16, "space-between")}>
            <div style={hstack(16)}>
              <div
                style={{ background: "#3b82f6", padding: 12, borderRadius: 12 }}
              >
                <FileImage size={32} color="#fff" />
              </div>
              <div>
                <h1 style={{ ...title, fontSize: 24 }}>
                  어깨 웰니스 지원 AI 시스템
                </h1>
                <p style={{ ...text }}>초음파 영상 기반 자동 진단</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main style={{ flex: 1, marginBottom: 70 }}>
        <div style={{ ...container, paddingTop: 32, paddingBottom: 32 }}>
          {/* 메인 레이아웃 */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 32 }}>
            {/* 업로드 섹션 */}
            <div>
              <div style={{ ...card, ...pad, position: "sticky", top: 16 }}>
                <h3 style={{ ...title, fontSize: 18, color: "#1f2937" }}>
                  초음파 이미지 업로드
                </h3>
                <div
                  style={{
                    border: "2px dashed #93c5fd",
                    borderRadius: 12,
                    padding: 32,
                    textAlign: "center",
                    marginTop: 16,
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    style={{ display: "none" }}
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    style={{ cursor: "pointer", display: "inline-block" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Upload
                        size={48}
                        color="#3b82f6"
                        style={{ marginBottom: 12 }}
                      />
                      <p
                        style={{
                          margin: 0,
                          fontSize: 16,
                          fontWeight: 600,
                          color: "#374151",
                          marginBottom: 6,
                        }}
                      >
                        초음파 이미지 업로드
                      </p>
                      <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>
                        (JPG, PNG 형식 1개 파일)
                      </p>
                    </div>
                  </label>
                  <div style={{ marginTop: 12 }}>
                    <button
                      type="button"
                      onClick={loadDemoImage}
                      style={{ ...btnGhost }}
                    >
                      <Upload size={16} />
                      <span style={{ marginLeft: 6 }}>데모 이미지 사용</span>
                    </button>
                  </div>
                </div>

                {uploadedImages.length > 0 && (
                  <div
                    style={{
                      marginTop: 24,
                      display: "grid",
                      gap: 8,
                      maxHeight: 384,
                      overflowY: "auto",
                    }}
                  >
                    <h4
                      style={{ margin: 0, color: "#374151", fontWeight: 600 }}
                    >
                      업로드된 파일 ({uploadedImages.length}개)
                    </h4>
                    {uploadedImages.map((image) => (
                      <div
                        key={image.id}
                        style={{
                          ...hstack(12, "space-between"),
                          padding: 12,
                          background: "#f9fafb",
                          borderRadius: 10,
                        }}
                      >
                        <div style={hstack(12)}>
                          <img
                            src={image.preview}
                            alt="preview"
                            style={{
                              width: 48,
                              height: 48,
                              objectFit: "cover",
                              borderRadius: 8,
                            }}
                          />
                          <div>
                            <p
                              style={{
                                margin: 0,
                                fontSize: 13,
                                fontWeight: 600,
                                maxWidth: 180,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {image.name}
                            </p>
                            <p
                              style={{
                                margin: 0,
                                fontSize: 12,
                                color: "#6b7280",
                              }}
                            >
                              {image.type}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeImage(image.id)}
                          style={{
                            background: "transparent",
                            border: "none",
                            color: "#ef4444",
                            cursor: "pointer",
                            padding: 4,
                          }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {uploadedImages.length > 0 && !analysisComplete && (
                  <button
                    onClick={handleAnalysis}
                    disabled={isAnalyzing}
                    style={{
                      ...btnPrimary,
                      width: "100%",
                      marginTop: 24,
                      justifyContent: "center",
                      opacity: isAnalyzing ? 0.6 : 1,
                    }}
                  >
                    {isAnalyzing ? (
                      <span>분석 중...</span>
                    ) : (
                      <>
                        <Eye size={20} />
                        <span>AI 진단 시작</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* 결과 섹션 */}
            <div>
              {isAnalyzing && (
                <div style={{ ...card, ...pad, textAlign: "center" }}>
                  <div
                    style={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      border: "6px solid #bfdbfe",
                      borderTopColor: "#3b82f6",
                      margin: "0 auto 16px",
                      animation: "spin 1s linear infinite",
                    }}
                  />
                  <h3
                    style={{
                      ...title,
                      fontSize: 20,
                      color: "#374151",
                      marginBottom: 8,
                    }}
                  >
                    AI 분석 진행 중
                  </h3>
                  <p style={{ ...text, fontSize: 16 }}>
                    업로드하신 {uploadedImages.length}개 이미지를 분석하고
                    있습니다...
                  </p>
                  <style>{`@keyframes spin { from { transform: rotate(0deg);} to { transform: rotate(360deg);} }`}</style>
                </div>
              )}
              {analysisComplete && overallResult && (
                <div
                  style={{
                    ...pad,
                    ...card,
                    marginBottom: 32,
                    border: `2px solid ${
                      overallResult.colors[overallResult.risk].border
                    }`,
                    background: overallResult.colors[overallResult.risk].bg,
                  }}
                >
                  <div style={hstack(16, "space-between")}>
                    <div style={{ ...hstack(16), flex: 1 }}>
                      <div
                        style={{
                          padding: 12,
                          borderRadius: 12,
                          color: overallResult.colors[overallResult.risk].text,
                        }}
                      >
                        {getRiskIcon(overallResult.risk)}
                      </div>
                      <div style={{ flex: 1 }}>
                        {!isEditingOverall ? (
                          <div style={vstack(16)}>
                            <div>
                              <label
                                style={{
                                  display: "block",
                                  fontSize: 14,
                                  fontWeight: 500,
                                  color: "#374151",
                                  marginBottom: 8,
                                }}
                              >
                                위험도 수준
                              </label>
                              <div
                                role="group"
                                aria-label="위험도 선택"
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 12,
                                }}
                              >
                                {[
                                  {
                                    key: "low",
                                    color: "black",
                                    bg: "green",
                                    label: "낮음",
                                  },
                                  {
                                    key: "medium",
                                    color: "black",
                                    bg: "yellow",
                                    label: "보통",
                                  },
                                  {
                                    key: "high",
                                    color: "black",
                                    bg: "red",
                                    label: "높음",
                                  },
                                ].map((opt) => {
                                  const selected =
                                    overallResult?.risk === opt.key;
                                  return (
                                    <button
                                      key={opt.key}
                                      type="button"
                                      onClick={() => {
                                        const rec =
                                          opt.key === "high"
                                            ? "즉시 전문의 상담을 권장합니다"
                                            : opt.key === "medium"
                                            ? "추가 검사를 고려해보세요"
                                            : "정기적인 관찰을 권장합니다";
                                        setOverallResult((prev) => ({
                                          ...prev,
                                          risk: opt.key,
                                          recommendation: rec,
                                        }));
                                      }}
                                      aria-pressed={selected}
                                      title={opt.label}
                                      style={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: "50%",
                                        border: `2px solid ${
                                          selected ? opt.color : "black"
                                        }`,
                                        background: opt.bg,
                                        boxShadow: selected
                                          ? `0 0 0 4px ${opt.bg}`
                                          : "none",
                                        cursor: "pointer",
                                      }}
                                    />
                                  );
                                })}
                              </div>
                            </div>
                            {/* <div style={hstack(8)}>
                              <button
                                onClick={saveOverallEdit}
                                style={btnPrimary}
                              >
                                <Save size={16} />
                                <span>저장</span>
                              </button>
                              <button
                                onClick={cancelOverallEdit}
                                style={btnGray}
                              >
                                <XCircle size={16} />
                                <span>취소</span>
                              </button>
                            </div> */}
                            <h2
                              style={{
                                margin: 0,
                                fontSize: 20,
                                fontWeight: 700,
                                color:
                                  overallResult.colors[overallResult.risk].text,
                              }}
                            >
                              전체 위험도:{" "}
                              {overallResult.colors[overallResult.risk].label}
                            </h2>
                          </div>
                        ) : (
                          <></>
                        )}
                      </div>
                    </div>
                    <div style={hstack(8)}>
                      {!isEditingOverall && (
                        <div
                        // onClick={startEditingOverall}
                        // style={btnPrimary}
                        >
                          {/* <Edit2 size={16} />
                          <span>편집</span> */}
                        </div>
                      )}
                      <button onClick={resetAnalysis} style={btnGhost}>
                        <Trash2 size={16} />
                        <span>새로운 진단</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {analysisComplete && individualResults.length > 0 && (
                <div style={vstack(24)}>
                  {individualResults.map((result, index) => {
                    const image = imageMap.get(result.id);
                    const rc = (overallResult?.colors || RISK_COLORS)[
                      result.riskLevel
                    ];
                    return (
                      <div
                        key={result.id}
                        style={{ ...card, overflow: "hidden" }}
                      >
                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <div style={pad}>
                            <button
                              type="button"
                              onClick={() => handleImageClick(result.id)}
                              style={{
                                ...text,
                                fontSize: 13,
                                textAlign: "left",
                                marginTop: 8,
                              }}
                            >
                              {image?.name || `demo-${index + 1}.png`}
                            </button>
                          </div>
                          <div
                            style={{ ...pad, borderTop: "1px solid #f3f4f6" }}
                          >
                            <div style={hstack(8, "space-between")}>
                              <h4
                                style={{
                                  ...title,
                                  fontSize: 18,
                                  color: "#1f2937",
                                }}
                              >
                                이미지 분석 결과
                              </h4>
                              <div style={hstack(8)}>
                                {editingImageId !== result.id && (
                                  <button
                                    onClick={() => startEditingImage(result.id)}
                                    style={btnPrimary}
                                  >
                                    <Edit2 size={16} />
                                    <span>편집</span>
                                  </button>
                                )}
                              </div>
                            </div>

                            {editingImageId === result.id ? (
                              <div style={vstack(16)}>
                                <div style={{ margin: "10px 0" }}>
                                  <label
                                    style={{
                                      display: "block",
                                      fontSize: 14,
                                      fontWeight: 500,
                                      color: "#374151",
                                      marginBottom: 8,
                                    }}
                                  >
                                    진단 결과
                                  </label>
                                  <input
                                    type="text"
                                    value={tempEditData.condition}
                                    onChange={(e) =>
                                      setTempEditData((p) => ({
                                        ...p,
                                        condition: e.target.value,
                                      }))
                                    }
                                    placeholder="진단 결과를 입력하세요"
                                    style={{
                                      width: "100%",
                                      border: "1px solid #d1d5db",
                                      borderRadius: 8,
                                      padding: "8px 12px",
                                    }}
                                  />
                                </div>
                                <div style={hstack(8)}>
                                  <button
                                    onClick={saveImageEdit}
                                    style={btnPrimary}
                                  >
                                    <Save size={16} />
                                    <span>저장</span>
                                  </button>
                                  <button
                                    onClick={cancelImageEdit}
                                    style={btnGray}
                                  >
                                    <XCircle size={16} />
                                    <span>취소</span>
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div style={vstack(16)}>
                                <div>
                                  <h5
                                    style={{
                                      margin: 0,
                                      fontWeight: 600,
                                      color: "#374151",
                                      marginBottom: 8,
                                    }}
                                  >
                                    진단 결과
                                  </h5>
                                  <p
                                    style={{
                                      margin: 0,
                                      fontSize: 16,
                                      color: "#111827",
                                      background: "#f9fafb",
                                      padding: 12,
                                      borderRadius: 8,
                                    }}
                                  >
                                    {result.condition}
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      {fullscreenImage && (
        <div
          onClick={closeFullscreen}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.8)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
        >
          {/* ✅ 화면 기준 우상단 고정 닫기 버튼 */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeFullscreen();
            }}
            aria-label="닫기"
            style={{
              position: "fixed",
              top: 16,
              right: 16,
              background: "rgba(0,0,0,0.6)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              padding: "10px 12px",
              cursor: "pointer",
              zIndex: 10000,
            }}
          >
            <X size={18} />
          </button>

          {/* 이미지 컨테이너 (이 안쪽의 예전 버튼은 삭제) */}
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "relative",
              maxWidth: "95vw",
              maxHeight: "95vh",
            }}
          >
            <img
              src={fullscreenImage.preview}
              alt={fullscreenImage.name || "preview"}
              style={{
                display: "block",
                maxWidth: "95vw",
                maxHeight: "95vh",
                width: "500px",
                height: "500px",
                objectFit: "contain",
                borderRadius: 12,
                boxShadow: "0 20px 50px rgba(0,0,0,0.5)",
              }}
            />
          </div>
        </div>
      )}
      <footer
        style={{
          position: "fixed",
          bottom: 0,
          width: "100%",
          background: "#1f2937",
          color: "#fff",
          padding: "32px 0",
        }}
      >
        <div style={{ ...container, textAlign: "center" }}>
          <p style={{ margin: 0, color: "#d1d5db" }}>
            ※ 본 시스템의 결과는 진단을 보조하기 위한 참고 자료이며, 최종 진단과
            치료 결정은 반드시 전문의의 의학적 판단에 의해 이루어져야 합니다.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default App;
